import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 } from 'uuid';
import { useWebSocket } from './useWebSocket';
import { handleWebsocketMessageThunk } from 'components/ChatDialog/store/thunks/handleWebsocketMessage';
import { setChatWebsocket } from 'components/ChatDialog/store/slices/chatSlice';

/**
 * Custom hook for managing chat WebSocket connection with automatic resubscription
 * @returns {Object} - { chatWebsocketRef, storeThreads }
 */
export const useChatWebSocket = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.root.user);
  const chatThreadsRef = useRef([]);

  const subscribeToChatThreads = (ws, threads) => {
    if (!ws || !threads.length) return;

    threads.forEach((thread) => {
      const socketValidObject = {
        newThreadConnection: {
          id: v4(),
          userId: user.id,
          threadId: thread.id,
          isActive: false,
        },
      };
      ws.send(JSON.stringify(socketValidObject));
    });
  };

  const { wsRef: chatWebsocketRef } = useWebSocket({
    url: process.env.REACT_APP_CHAT_WEBSOCKET_URL,
    enabled: !!user?.id,
    onMessage: (event) => {
      dispatch(handleWebsocketMessageThunk({ event, user }));
    },
    onOpen: (event, ws) => {
      window.chatWebsocketRef = chatWebsocketRef;
      dispatch(setChatWebsocket(ws.url));

      // Resubscribe to all threads on reconnect
      if (chatThreadsRef.current.length > 0) {
        subscribeToChatThreads(ws, chatThreadsRef.current);
      }
    },
    onClose: () => {
      delete window.chatWebsocketRef;
    },
  });

  const storeThreads = (threads) => {
    chatThreadsRef.current = threads;
  };

  return {
    chatWebsocketRef,
    storeThreads,
  };
};
